const ELEVENLABS_API_KEY = import.meta.env.VITE_ELEVENLABS_API_KEY || ''
// Available voices: Rachel (21m00Tcm4TlvDq8ikWAM), Charlotte (XB0fDUnXU5powFXDhCwa), or Emily (LcfcDJNUP1GQjkzn1xUU)
const ELEVENLABS_VOICE_ID = '21m00Tcm4TlvDq8ikWAM' // Rachel voice
const ELEVENLABS_API_URL = 'https://api.elevenlabs.io/v1/text-to-speech'

interface ElevenLabsOptions {
  voiceId?: string
  modelId?: string
  stability?: number
  similarityBoost?: number
  style?: number
  useSpeakerBoost?: boolean
}

let currentAudio: HTMLAudioElement | null = null

export const elevenLabsService = {
  stopCurrentAudio() {
    if (currentAudio) {
      currentAudio.pause()
      currentAudio.currentTime = 0
      currentAudio = null
    }
  },

  async textToSpeech(
    text: string,
    options: ElevenLabsOptions = {}
  ): Promise<Blob | null> {
    // Check both module-level and runtime env var (Vite loads env vars at build time)
    const apiKey = import.meta.env.VITE_ELEVENLABS_API_KEY || ELEVENLABS_API_KEY || ''
    
    if (!apiKey || apiKey.trim() === '') {
      return null
    }

    try {
      const voiceId = options.voiceId || ELEVENLABS_VOICE_ID
      
      const response = await fetch(`${ELEVENLABS_API_URL}/${voiceId}`, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': apiKey
        },
        body: JSON.stringify({
          text,
          model_id: options.modelId || 'eleven_multilingual_v2',
          voice_settings: {
            stability: options.stability ?? 0.5,
            similarity_boost: options.similarityBoost ?? 0.75,
            style: options.style ?? 0.0,
            use_speaker_boost: options.useSpeakerBoost ?? true
          }
        })
      })

      if (!response.ok) {
        return null
      }

      const blob = await response.blob()
      return blob
    } catch (error) {
      return null
    }
  },

  async playAudio(blob: Blob): Promise<HTMLAudioElement> {
    this.stopCurrentAudio()
    
    const audioUrl = URL.createObjectURL(blob)
    const audio = new Audio(audioUrl)
    currentAudio = audio
    
    return new Promise((resolve, reject) => {
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl)
        if (currentAudio === audio) {
          currentAudio = null
        }
        resolve(audio)
      }
      audio.onerror = (error) => {
        URL.revokeObjectURL(audioUrl)
        if (currentAudio === audio) {
          currentAudio = null
        }
        reject(error)
      }
      audio.play().catch(reject)
    })
  },

  async speak(text: string, options?: ElevenLabsOptions): Promise<void> {
    // Replace "1031" with "ten thirty one" for proper pronunciation
    const speakableText = text.replace(/\b1031\b/gi, 'ten thirty one')
    
    const blob = await this.textToSpeech(speakableText, options)
    if (!blob) {
      return
    }
    
    try {
      await this.playAudio(blob)
    } catch (error) {
    }
  }
}
